import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  FolderKanban,
  CreditCard,
  FileText,
  Users,
  MessageSquare,
  Settings,
  LogOut,
  Moon,
  Sun,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

const clientNavItems: NavItem[] = [
  {
    href: '/dashboard',
    label: 'Overview',
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    href: '/dashboard/projects',
    label: 'Projects',
    icon: <FolderKanban className="w-5 h-5" />,
  },
  {
    href: '/dashboard/invoices',
    label: 'Invoices',
    icon: <CreditCard className="w-5 h-5" />,
  },
  {
    href: '/dashboard/notes',
    label: 'Meeting Notes',
    icon: <FileText className="w-5 h-5" />,
  },
];

const adminNavItems: NavItem[] = [
  {
    href: '/dashboard',
    label: 'Overview',
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    href: '/dashboard/clients',
    label: 'Clients',
    icon: <Users className="w-5 h-5" />,
    adminOnly: true,
  },
  {
    href: '/dashboard/projects',
    label: 'All Projects',
    icon: <FolderKanban className="w-5 h-5" />,
  },
  {
    href: '/dashboard/invoices',
    label: 'Invoices',
    icon: <CreditCard className="w-5 h-5" />,
  },
  {
    href: '/dashboard/inquiries',
    label: 'Inquiries',
    icon: <MessageSquare className="w-5 h-5" />,
    adminOnly: true,
  },
  {
    href: '/dashboard/settings',
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { isAdmin, signOut, profile } = useAuth();
  const { actualTheme, toggleTheme } = useTheme();
  const location = useLocation();

  const navItems = isAdmin ? adminNavItems : clientNavItems;

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
    // Force full page reload to clear all state
    window.location.href = '/login';
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-obsidian/80 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 z-50 h-full w-64 bg-obsidian-50 border-r border-slate-700',
          'transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700">
            <Link
              to="/"
              className="flex items-center gap-2"
            >
              <img
                src={actualTheme === 'dark' ? '/logo-icon-dark.svg' : '/logo-icon-light.svg'}
                alt="ChurnLogix"
                className="w-8 h-8"
              />
              <span className="text-lg font-heading font-bold">
                <span className="text-signal">CHURN</span>
                <span className="text-slate-400">LOGIX</span>
              </span>
            </Link>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 border-b border-slate-700">
            <p className="text-sm font-medium text-signal truncate">
              {profile?.company_name || 'Welcome'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {isAdmin ? 'Administrator' : 'Client'}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    onClick={onClose}
                    className={clsx(
                      'flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors',
                      isActive(item.href)
                        ? 'bg-amber/10 text-amber'
                        : 'text-slate-300 hover:bg-obsidian-100 hover:text-signal'
                    )}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Bottom Actions */}
          <div className="px-3 py-4 border-t border-slate-700">
            <button
              onClick={toggleTheme}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded text-sm font-medium text-slate-300 hover:bg-obsidian-100 hover:text-signal transition-colors"
            >
              {actualTheme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
              {actualTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded text-sm font-medium text-red-400 hover:bg-red-900/20 transition-colors mt-1"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
